import React, { useState } from "react";
import axios from "axios";
import { Sparkles, X, ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Banner shown at the top of the Dashboard when the user's Pro subscription
 * has ended (trial expired without card OR paid subscription cancelled).
 * Their cards are preserved — but Simulator + Optimizer re-lock and any card
 * count over the free limit is auto-hidden.
 */
const ProExpiredBanner = ({ user, onDismiss, onUpgrade }) => {
  const [busy, setBusy] = useState(false);

  if (!user) return null;
  if (!user.pro_expired_at) return null;
  if (user.pro_expired_dismissed_at) return null;
  if (user.plan === "pro") return null;

  const reasonLabel =
    user.pro_expired_reason === "trial_expired"
      ? "Your free trial ended without a payment method."
      : "Your Pro subscription was cancelled.";

  const dismiss = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/auth/dismiss_pro_expired`,
        {},
        { withCredentials: true }
      );
      if (onDismiss) onDismiss();
    } catch (e) {
      // ignore — user can dismiss on next load
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.08] to-transparent p-4 sm:p-5 mb-6 relative"
      data-testid="pro-expired-banner"
    >
      <button
        type="button"
        onClick={dismiss}
        disabled={busy}
        aria-label="Dismiss"
        data-testid="pro-expired-dismiss"
        className="absolute top-3 right-3 text-slate-300 hover:text-white transition-colors"
      >
        <X size={16} />
      </button>
      <div className="flex items-start gap-3 sm:gap-4 pr-6">
        <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center flex-shrink-0">
          <Sparkles size={16} className="text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300 font-bold">
            Pro expired
          </div>
          <h3
            className="mt-1 text-white text-base sm:text-lg font-semibold tracking-tight"
            style={{ fontFamily: "'Outfit', sans-serif" }}
          >
            You're back on the Free plan
          </h3>
          <p className="mt-1.5 text-xs sm:text-sm text-slate-300 leading-relaxed">
            {reasonLabel} Your cards are safe, but Simulator and Optimizer are
            locked and you're capped at 2 cards. Add a payment method to keep Pro.
          </p>
          <button
            type="button"
            onClick={onUpgrade}
            data-testid="pro-expired-upgrade"
            className="mt-3 inline-flex items-center gap-2 rounded-full bg-[#00E676] hover:bg-[#00C863] text-[#060B19] font-semibold px-4 py-2 text-xs transition-[transform,background-color] duration-200 hover:-translate-y-0.5 shadow-[0_10px_30px_-12px_rgba(0,230,118,0.6)]"
          >
            Restart Pro <ArrowRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProExpiredBanner;
